/**
 * HOS v4.3 — Intro screen. Routes both form and sandbox candidates here on phase 0.
 */
import React, { useMemo } from "react";
import { Loader2, Clock, Lock, Eye, AlertTriangle, ArrowRight } from "lucide-react";

export default function HOSIntroScreen({ data, onBegin, beginning, error }) {
    const meta = data?.template?.content?.metadata || {};
    const mode = data?.mode || "form";
    const totalMin = Math.round((meta.total_time_seconds || 1800) / 60);
    const candidateFirst = (data?.candidate?.name || "").split(" ")[0] || "there";

    const acts = useMemo(() => {
        if (mode === "form") return (data?.template?.content?.content || []).filter(s => s.type === "section");
        const sb = (data?.template?.content?.content || []).find(s => s.type === "sandbox_simulation");
        return sb ? [sb] : [];
    }, [data, mode]);

    return (
        <div className="min-h-screen bg-background" data-testid="hos-intro-screen">
            <header className="border-b border-border/40 bg-card">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div>
                        <h1 className="text-sm font-display font-bold text-white tracking-wide">THCO · Hands-On Simulation</h1>
                        <p className="text-[11px] text-gray-600 font-mono">{data?.template?.job_title} · {data?.candidate?.email}</p>
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono">v{meta.version || "4.3"} · {mode === "form" ? "form" : "sandbox"}</div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-12">
                <p className="text-xs uppercase tracking-[0.25em] text-gray-500 font-mono mb-3">Pre-simulation briefing</p>
                <h2 className="text-3xl sm:text-4xl font-display font-bold text-white mb-3 leading-tight">Welcome, {candidateFirst}.</h2>
                <p className="text-sm text-gray-400 leading-relaxed mb-8 max-w-xl">
                    {mode === "form" ? (
                        <span>This is a {totalMin}-minute narrative simulation. You'll work through five acts of a Monday-to-Friday week at a fictional company. Each act introduces new information; some of what you do in Act 1 will be wrong by Act 2. Write what you'd actually send to stakeholders, not paraphrases.</span>
                    ) : (
                        <span>This is a {totalMin}-minute hands-on sandbox. You'll have a small codebase with deliberate bugs, a diagnostic console, and a Run Tests button. Edit what needs editing. Document your reasoning in NOTES.md as you go. You may run the tests up to 10 times.</span>
                    )}
                </p>

                {mode === "form" ? (
                    <div className="space-y-3 mb-10">
                        {acts.map((s) => (
                            <div key={s.id} className="flex items-start gap-4 border border-border/40 bg-card px-5 py-4" data-testid={`hos-intro-act-${s.section_number}`}>
                                <div className="text-xs font-mono text-gray-500 w-14 shrink-0 pt-1">{`Act ${s.section_number}`}</div>
                                <div className="flex-1">
                                    <div className="flex items-baseline justify-between mb-1">
                                        <h3 className="text-sm font-display font-bold text-white">{s.title}</h3>
                                        <span className="text-[11px] text-gray-500 font-mono flex items-center gap-1"><Clock className="w-3 h-3" />{Math.round(((s.timer || {}).duration_seconds || 0) / 60)} min</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="mb-10 border border-border/40 bg-card p-5" data-testid="hos-intro-sandbox-card">
                        <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-2">The brief</p>
                        <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap mb-4">{acts[0]?.readme || ""}</p>
                        <div className="grid grid-cols-3 gap-4 text-[11px] font-mono">
                            <Stat label="Files" value={(acts[0]?.files || []).length} />
                            <Stat label="Test runs" value={acts[0]?.max_test_runs || 10} />
                            <Stat label="Console cmds" value={((acts[0]?.diagnostic_console || {}).available_commands || []).length} />
                        </div>
                    </div>
                )}

                <div className="bg-card border border-border/40 p-5 mb-10">
                    <h4 className="text-xs uppercase tracking-wider font-mono text-gray-400 mb-3">Before you begin</h4>
                    <ul className="space-y-2.5 text-xs text-gray-400 leading-relaxed">
                        <li className="flex gap-3"><Lock className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5" /><span>The clock is enforced server-side. {mode === "form" ? "Once you submit an act, it locks." : "Once you submit, the sandbox locks."}</span></li>
                        <li className="flex gap-3"><Eye className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5" /><span>{mode === "form" ? "We track typing cadence, paste events, and tab focus loss." : "We track file edits, console commands, and test run timing. The Run Tests evaluator is currently MOCKED — results are deterministic placeholders."}</span></li>
                        <li className="flex gap-3"><AlertTriangle className="w-3.5 h-3.5 text-amber-300/80 shrink-0 mt-0.5" /><span>{mode === "form" ? "Some acts introduce information that contradicts an earlier act. That is intentional." : "Some of the baseline test failures are misleading and some files contain trap code. Calling out which tests you don't trust earns credit."}</span></li>
                    </ul>
                </div>

                {error && (
                    <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 mb-6" data-testid="hos-intro-error">
                        <p className="text-xs text-red-300">{error}</p>
                    </div>
                )}

                <div className="flex items-center justify-between">
                    <p className="text-[11px] text-gray-600 font-mono">By beginning, you confirm you are <span className="text-gray-400">{data?.candidate?.name}</span>.</p>
                    <button
                        onClick={onBegin}
                        disabled={beginning}
                        className="px-6 py-3 bg-white text-black text-sm font-semibold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-wait flex items-center gap-2"
                        data-testid="hos-begin-btn"
                    >
                        {beginning ? <><Loader2 className="w-4 h-4 animate-spin" />Starting…</> : <>{mode === "form" ? "Begin Act 1" : "Enter sandbox"} <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </div>
            </main>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <p className="text-gray-500 uppercase tracking-wider text-[10px]">{label}</p>
            <p className="text-white text-base mt-0.5">{value}</p>
        </div>
    );
}
